import { type ChangeEvent, type KeyboardEvent, type FocusEvent } from 'react';

import { KEYBOARD_CODES } from '../../../../constants';
import type { HTMLInputProps, TextInputProps } from '../types';
import { useControlledInput } from '../useControlledInput';
import { excludeWrapperProps } from '../utils';

/**
 * Get the basic props for the text input (e.g. BasicInput component)
 * This hook is used to make the main component headless and to handle all the logic
 */
export const useBasicTextInputProps = (
  props: TextInputProps
): {
  basicInputProps: HTMLInputProps;
  handleChanged: (value: string) => void;
} => {
  const { value, syncInputValue, commitInputValue } = useControlledInput(
    props.value,
    props.initialValue
  );
  const {
    onChanging,
    onChanged,
    onBlur,
    initialValue: _,
    ...inputProps
  } = excludeWrapperProps(props);

  /**
   * Handle the change event on the input: update the value and sync the new value to the state
   * @param e - The change event
   */
  const handleChanging = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    onChanging?.(value);
    syncInputValue(value);
  };

  const handleChanged = (newValue: string) => {
    commitInputValue(newValue);
    onChanged?.(newValue);
  };

  const handleBlur = (
    e: FocusEvent<HTMLInputElement> | KeyboardEvent<HTMLInputElement>
  ) => {
    if (!(e.target instanceof HTMLInputElement)) {
      return;
    }

    if (e.target) {
      e.target.scrollLeft = 0;
    }

    const newValue = e.target.value;

    commitInputValue(newValue);

    if (e.target.validity.valid) {
      onChanged?.(newValue);
    }
    onBlur?.();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!(e.target instanceof HTMLInputElement)) {
      return;
    }

    switch (e.key) {
      case KEYBOARD_CODES.ENTER:
      case KEYBOARD_CODES.NUMPAD_ENTER:
        e.preventDefault();
        handleBlur(e);
        e.target.blur();
        break;
    }
  };

  return {
    basicInputProps: {
      ...inputProps,
      'aria-invalid': props.state === 'critical' ? true : undefined,
      value: value ?? '',
      onChange: handleChanging,
      onBlur: handleBlur,
      onKeyDown: handleKeyDown,
    },
    handleChanged,
  };
};
